import React from 'react'
import { X } from 'lucide-react'
import { usePlayerPreferences } from '../../providers/PlayerPreferencesProvider'
import { formatRarityLabel, getRarityBadgeClasses } from '../../utils/rarityBadges'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'
// AGC: Removed SwipeCarousel - displaying both cards vertically

/**
 * AGC: Minimal representation of the data needed to describe a single battle turn.
 */
export interface BattleTurnSnapshot {
  turnNumber?: number
  yourChoice?: string | null
  opponentChoice?: string | null
  yourElement?: string | null
  opponentElement?: string | null
  yourDamage?: number
  opponentDamage?: number
  yourEnergy?: number
  opponentEnergy?: number
  yourBuffs?: Array<{ label: string; status?: 'active' | 'ready' | 'used' }>
  opponentBuffs?: Array<{ label: string; status?: 'active' | 'ready' | 'used' }>
  yourBreakdown?: TurnBreakdownSummary
  opponentBreakdown?: TurnBreakdownSummary
}

/**
 * AGC: Subset of damage breakdown fields shared across battle implementations.
 */
export interface TurnBreakdownSummary {
  baseName?: string
  baseDamage?: number
  levelBonus?: number
  elementBonus?: number
  elementName?: string
  rarityBonus?: number
  rarityName?: string
  hasteBonus?: number
  rawDamage?: number
  defense?: number
  afterDefense?: number
  pierceReduction?: number
  shieldReduction?: number
  frostReduction?: number
  finalDamage?: number
  // AGC: Mana potion boost (NEW - 2025-11-28)
  manaBoost?: number             // Mana boost multiplier (e.g., 1.05 = +5% buff power)
  // AGC: Potion effect fields (NEW - 2025-11-25)
  potionHealing?: number         // Energy restored by healing potion
  potionPoisonReceived?: number  // Damage taken from opponent's poison potion
  potionShieldReduction?: number // Damage blocked by shield potion
  // AGC: Double Strike potion effect (NEW - 2025-11-27)
  doubleStrikeBonus?: number     // Bonus damage from Double Strike potion
  doubleStrikePercent?: number   // Percentage of Double Strike bonus (e.g. 25 for +25%)
}

const CHOICE_IMAGE_MAP: Record<string, { src: string; alt: string }> = {
  rock: { src: '/rock.png', alt: 'Rock hand' },
  paper: { src: '/paper.png', alt: 'Paper hand' },
  scissors: { src: '/scissor.png', alt: 'Scissors hand' },
}

const resolveImagePath = (assetPath: string): string => {
  if (!assetPath) {
    return assetPath
  }

  if (/^https?:\/\//i.test(assetPath)) {
    return assetPath
  }

  try {
    const base = import.meta.env?.BASE_URL ?? '/'
    const normalizedBase = typeof base === 'string' && base.endsWith('/') ? base : `${base}/`
    const trimmedPath = assetPath.replace(/^\/+/, '')
    return `${normalizedBase}${trimmedPath}`
  } catch {
    return assetPath
  }
}

interface BattleTurnModalProps {
  isOpen: boolean
  turn: BattleTurnSnapshot | null
  onClose: () => void
  turnTimeLeft?: number
  contextLabel?: string
  isLastTurn?: boolean // AGC: Indicates if this is the final turn (battle ending)
}

/**
 * AGC: Full-screen modal that highlights the latest battle turn details before the player proceeds.
 */
export const BattleTurnModal: React.FC<BattleTurnModalProps> = ({
  isOpen,
  turn,
  onClose,
  turnTimeLeft,
  contextLabel = 'Turn Breakdown',
  isLastTurn = false,
}) => {
  const { preferences, updatePreferences } = usePlayerPreferences()
  const { isBaseAppContext } = useFeatureFlags()

  // AGC: Auto-close on last turn to show win/defeat modal
  // AGC: Must be called before early returns to follow Rules of Hooks
  React.useEffect(() => {
    if (isLastTurn && isOpen && turn) {
      const timer = setTimeout(() => {
        onClose()
      }, 2000) // AGC: Auto-close after 2 seconds on last turn
      return () => clearTimeout(timer)
    }
  }, [isLastTurn, isOpen, onClose, turn])

  if (!isOpen || !turn) {
    return null
  }

  if (!preferences.showTurnBreakdownModal) {
    return null
  }

  const handleShowNextToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const shouldShow = event.target.checked
    updatePreferences({ showTurnBreakdownModal: shouldShow })
    if (!shouldShow) {
      onClose()
    }
  }

  // AGC: Create carousel items for horizontal scrolling
  const carouselItems = [
    {
      label: 'YOU',
      type: 'you' as const,
      params: {
        choice: turn.yourChoice,
        element: turn.yourElement,
        damage: turn.yourDamage,
        energy: turn.yourEnergy,
        buffs: turn.yourBuffs,
        breakdown: turn.yourBreakdown,
      },
    },
    {
      label: 'OPPONENT',
      type: 'opponent' as const,
      params: {
        choice: turn.opponentChoice,
        element: turn.opponentElement,
        damage: turn.opponentDamage,
        energy: turn.opponentEnergy,
        buffs: turn.opponentBuffs,
        breakdown: turn.opponentBreakdown,
      },
    },
  ]

  const youSideParams = carouselItems.find((item) => item.type === 'you')?.params
  const opponentSideParams = carouselItems.find((item) => item.type === 'opponent')?.params

  const renderBuffChips = (buffs?: Array<{ label: string; status?: 'active' | 'ready' | 'used' }>) => {
    if (!buffs || buffs.length === 0) {
      return <p className="text-[10px] text-slate-400">None</p>
    }

    return (
      <div className="flex flex-wrap gap-1">
        {buffs.map((buff, index) => {
          const status = buff.status ?? 'ready'
          const color =
            status === 'active'
              ? 'bg-green-600 text-white'
              : status === 'used'
              ? 'bg-slate-600 text-slate-300'
              : 'bg-red-700 text-red-100'
          return (
            <span key={`${buff.label}-${index}`} className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${color}`}>
              {buff.label}
            </span>
          )
        })}
      </div>
    )
  }

  const renderRarityChip = (rarityName?: string | null) => {
    const classes = getRarityBadgeClasses(rarityName)
    if (!classes) {
      return null
    }
    const label = formatRarityLabel(rarityName)
    return (
      <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${classes}`}>
        {label}
      </span>
    )
  }

  const renderDamageDetails = (type: 'you' | 'opponent', params: TurnSideParams, yourElement?: string | null, opponentElement?: string | null, otherBreakdown?: TurnBreakdownSummary) => {
    const breakdown = params.breakdown
    if (!breakdown) {
      return null
    }

    // AGC: FIX 2025-11-29 - Correct order: HERO → RARITY → BUFF (matches game engine)
    const { summary: baseLine, baseTotal } = buildBaseSummary(breakdown, type === 'you' ? yourElement : opponentElement, type === 'you' ? opponentElement : yourElement)
    const { summary: raritySummary, total: rarityTotal } = buildRaritySummary(breakdown, baseTotal)
    const { summary: buffSummary, total: buffTotal } = buildBuffSummary(breakdown, rarityTotal, params.buffs)
    const defenseLabel = type === 'you' ? 'Opp Def' : 'Your Def'
    const defenseMultiplier = breakdown.defense ? breakdown.defense.toFixed(2) : '1.00'
    const defenseValue = Math.round(breakdown.afterDefense ?? buffTotal * (breakdown.defense ?? 1))
    const finalDamageValue = Math.round(
      typeof params.damage === 'number'
        ? params.damage
        : breakdown.finalDamage ?? breakdown.afterDefense ?? defenseValue
    )
    // AGC: Pass otherBreakdown to check for mana in correct side
    const specialMessages = buildSpecialEffectLines(type, breakdown, otherBreakdown)

    return (
      <div 
        className="bg-slate-800 rounded-lg p-2 space-y-0.5 text-[10px]"
        {...(type === 'you' ? { 'data-tour': 'simulation-breakdown-area-full' } : {})}
      >
        <p className="text-slate-100 leading-tight">{baseLine}</p>
        <p className="text-slate-200 leading-tight">Rarity: {raritySummary}</p>
        <p className="text-slate-200 leading-tight">Buff: {buffSummary}</p>
        <p className="text-slate-300 leading-tight">
          {defenseLabel} (×{defenseMultiplier}) = {defenseValue}
        </p>
        {specialMessages.map((message, idx) => (
          <p key={`${message.text}-${idx}`} className={`leading-tight ${
            message.tone === 'danger' ? 'text-red-300' : 
            message.tone === 'success' ? 'text-green-300' : 
            'text-blue-300'
          }`}>
            {message.text}
          </p>
        ))}
        <p className={`font-bold leading-tight ${type === 'you' ? 'text-green-300' : 'text-red-300'}`}>Final: {finalDamageValue} dmg</p>
      </div>
    )
  }

  const renderSide = (
    label: string,
    params: TurnSideParams,
    type: 'you' | 'opponent',
    compact: boolean,
    comparison?: TurnSideParams,
  ) => {
    const rarityChip = renderRarityChip(params.breakdown?.rarityName)

    return (
      <div className="bg-slate-900 rounded-2xl p-3 space-y-2 border border-slate-700">
        {/* AGC: Header with label and energy bar */}
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-base font-semibold text-white">{label}</h4>
          <span className="text-xs text-slate-400">Energy: {numberOrDash(params.energy)}</span>
        </div>
        <div className={`h-1.5 ${compact ? 'w-full' : 'w-full'} bg-slate-700 rounded-full overflow-hidden`}>
          <div
            className={`h-full transition-all duration-300 ${getEnergyBarClass(params.energy)}`}
            style={{ width: `${clampPercentage(params.energy)}%` }}
          ></div>
        </div>

        {/* AGC: Hero + Element + Damage in ONE horizontal line */}
        <div className="flex items-center justify-between gap-3 py-2">
          {/* Hero + Element - with vs comparison for both cards (icons only, no text) */}
          <div className="flex items-center gap-2">
            {comparison ? (
              // AGC: Show vs comparison for both YOU and OPPONENT cards (icons only, no text)
              <>
                <div className="flex items-center gap-1">
                  {renderChoiceVisual(params.choice)}
                </div>
                <div className="flex items-center gap-1">
                  <p className="text-2xl">{elementEmoji(params.element)}</p>
                </div>
                <span className="text-white font-semibold tracking-wide mx-1">vs</span>
                <div className="flex items-center gap-1">
                  {renderChoiceVisual(comparison.choice)}
                </div>
                <div className="flex items-center gap-1">
                  <p className="text-2xl">{elementEmoji(comparison.element)}</p>
                </div>
              </>
            ) : (
              // AGC: Fallback layout if no comparison available
              <>
          <div className="flex items-center gap-1">
            {renderChoiceVisual(params.choice)}
          </div>
          <div className="flex items-center gap-1">
            <p className="text-2xl">{elementEmoji(params.element)}</p>
                </div>
              </>
            )}
          </div>
          
          {/* Damage */}
          <div className="text-right">
            <p className="text-white font-bold text-xl">={numberOrDash(params.damage)}</p>
            <p className="text-[9px] text-slate-400">dmg</p>
          </div>
        </div>

        {/* AGC: Buffs - more compact */}
        <div className="flex items-center gap-2">
          <p className="text-[10px] text-slate-400 whitespace-nowrap">Buffs:</p>
          {renderBuffChips(params.buffs)}
        </div>

        {/* AGC: Rarity - inline if present */}
        {rarityChip && (
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-slate-400 whitespace-nowrap">Rarity:</p>
            {rarityChip}
          </div>
        )}

        {/* AGC: Damage breakdown - more compact */}
        {/* AGC: Pass comparison breakdown to show mana in correct side */}
        {renderDamageDetails(type, params, turn.yourElement, turn.opponentElement, comparison?.breakdown)}
      </div>
    )
  }

  // AGC: Fullscreen modal container
  const modalContainerClass = 'relative w-full h-full bg-slate-950 border border-blue-500/40 shadow-2xl p-4 sm:p-6 space-y-4 overflow-y-auto'

  const headerRowClass = isBaseAppContext
    ? 'flex items-center justify-between gap-3 pr-6'
    : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pr-10'

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm">
      <div className={modalContainerClass}>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onClose()
          }}
          className="absolute top-4 right-4 p-2 rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
          aria-label="Close turn modal"
          data-tour="simulation-close-button"
        >
          <X className="w-4 h-4" />
        </button>
        <div className={headerRowClass}>
          <div>
            <p className="text-xs uppercase tracking-widest text-blue-400">{contextLabel}</p>
            <h3 className="text-2xl font-bold text-white flex items-center gap-2">
              Turn {turn.turnNumber ?? '—'}
              {(() => {
                // AGC: Calculate win/loss/draw status based on damage comparison
                const yourDamage = turn.yourDamage ?? 0
                const opponentDamage = turn.opponentDamage ?? 0
                let turnStatus: 'win' | 'loss' | 'draw' | null = null

                if (yourDamage > opponentDamage) {
                  turnStatus = 'win'
                } else if (yourDamage < opponentDamage) {
                  turnStatus = 'loss'
                } else if (yourDamage > 0 || opponentDamage > 0) {
                  // AGC: Only draw if there was actual damage
                  turnStatus = 'draw'
                }

                const statusText = turnStatus === 'win' ? 'YOU WIN' : turnStatus === 'loss' ? 'YOU LOST' : turnStatus === 'draw' ? 'DRAW' : ''
                const statusClass = turnStatus === 'win' ? 'text-green-400 animate-pulse' : turnStatus === 'loss' ? 'text-red-400 animate-pulse' : turnStatus === 'draw' ? 'text-yellow-400' : ''

                return statusText ? <span className={`text-lg font-semibold ${statusClass}`}>{statusText}</span> : null
              })()}
            </h3>
          </div>
          {typeof turnTimeLeft === 'number' && (
            <div className="text-amber-300 font-mono text-lg">[TIMER] {turnTimeLeft}s</div>
          )}
        </div>

        {/* AGC: Stacked cards - YOU card first, then OPPONENT card below */}
        <div className="w-full space-y-4">
          {/* YOU Card */}
          {youSideParams && (
        <div className="w-full" data-tour="simulation-your-card-full">
              {renderSide(
                'YOU',
                youSideParams,
                'you',
                isBaseAppContext,
                opponentSideParams, // AGC: Pass opponent params for vs comparison
              )}
              </div>
            )}
          
          {/* OPPONENT Card */}
          {opponentSideParams && (
            <div className="w-full" data-tour="simulation-opponent-card-full">
              {renderSide(
                'OPPONENT',
                opponentSideParams,
                'opponent',
                isBaseAppContext,
                youSideParams, // AGC: Pass you params for vs comparison
              )}
          </div>
          )}
        </div>

        <div
          className={
            isBaseAppContext
              ? 'grid grid-cols-2 gap-2 items-center'
              : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'
          }
        >
          <label className="inline-flex items-center gap-2 text-slate-200 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-500 bg-slate-800"
              checked={preferences.showTurnBreakdownModal}
              onChange={handleShowNextToggle}
            />
            Show this page next time
          </label>
          <button
            onClick={(event) => {
              event.stopPropagation()
              onClose()
            }}
            className={
              isBaseAppContext
                ? 'w-full bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold py-3 px-4 rounded-xl text-center'
                : 'w-full sm:w-auto bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-400 hover:to-blue-400 text-white font-bold py-3 px-6 rounded-xl transition-all text-center'
            }
            data-tour="simulation-continue-button"
          >
            Continue
          </button>
        </div>
        <p className="text-xs text-slate-400">Manage this preference anytime from Player Settings.</p>
      </div>
    </div>
  )
}

interface TurnSideParams {
  choice?: string | null
  element?: string | null
  damage?: number
  energy?: number
  buffs?: Array<{ label: string; status?: 'active' | 'ready' | 'used' }>
  breakdown?: TurnBreakdownSummary
}

const renderChoiceVisual = (choice?: string | null) => {
  const normalized = (choice ?? '').trim().toLowerCase()
  const mapping = CHOICE_IMAGE_MAP[normalized]

  if (!mapping) {
    return <p className="text-4xl">{choiceEmoji(choice)}</p>
  }

  return (
    <img
      src={resolveImagePath(mapping.src)}
      alt={mapping.alt}
      className="h-10 w-10 object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.45)] select-none pointer-events-none"
    />
  )
}

const numberOrDash = (value?: number) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return Math.round(value)
  }
  return '—'
}

const choiceEmoji = (choice?: string | null) => {
  switch ((choice ?? '').toLowerCase()) {
    case 'rock':
      return '🪨'
    case 'paper':
      return '📄'
    case 'scissors':
      return '✂️'
    default:
      return '❔'
  }
}

const elementEmoji = (element?: string | null) => {
  switch ((element ?? '').toLowerCase()) {
    case 'water':
      return '💧'
    case 'fire':
      return '🔥'
    case 'ice':
      return '❄️'
    case 'wind':
      return '💨'
    case 'none':
      return '⚪'
    default:
      return '—'
  }
}

// AGC: Determine element relationship based on Water > Fire > Ice > Wind > Water cycle
const determineElementRelationship = (attackerElement: string | null | undefined, defenderElement: string | null | undefined): 'WON' | 'LOST' | 'NEUTRAL' | 'DRAW' => {
  if (!attackerElement || !defenderElement) {
    return 'NEUTRAL'
  }
  
  const attacker = attackerElement.toUpperCase()
  const defender = defenderElement.toUpperCase()
  
  // AGC: Same element = DRAW
  if (attacker === defender) {
    return 'DRAW'
  }
  
  // AGC: Element advantage cycle: Water > Fire > Ice > Wind > Water
  const advantages: Record<string, string> = {
    WATER: 'FIRE',
    FIRE: 'ICE',
    ICE: 'WIND',
    WIND: 'WATER'
  }
  
  // AGC: Check if attacker beats defender
  if (advantages[attacker] === defender) {
    return 'WON'
  }
  
  // AGC: Check if defender beats attacker
  if (advantages[defender] === attacker) {
    return 'LOST'
  }
  
  // AGC: Non-adjacent elements (neither beats the other) = NEUTRAL
  return 'NEUTRAL'
}

const buildBaseSummary = (breakdown: TurnBreakdownSummary, attackerElement?: string | null, defenderElement?: string | null) => {
  const base = Math.round(breakdown.baseDamage ?? 0)
  const level = Math.round(breakdown.levelBonus ?? 0)
  const element = Math.round(breakdown.elementBonus ?? 0)
  
  // AGC: Determine HERO status from baseName (WIN/LOSE/DRAW or choice name)
  const baseNameUpper = (breakdown.baseName ?? '').toUpperCase()
  let heroStatus = 'DRAW'
  if (baseNameUpper.includes('WIN')) {
    heroStatus = 'WIN'
  } else if (baseNameUpper.includes('LOSE') || baseNameUpper.includes('LOST')) {
    heroStatus = 'LOSE'
  } else if (baseNameUpper.includes('DRAW')) {
    heroStatus = 'DRAW'
  }
  
  // AGC: Determine ELEMENT status from element relationship
  const elementRelationship = determineElementRelationship(attackerElement, defenderElement)
  let elementStatus = elementRelationship
  let elementValue = '0'
  
  if (elementRelationship === 'WON') {
    elementValue = `+${Math.abs(element)}`
  } else if (elementRelationship === 'LOST') {
    elementValue = `-${Math.abs(element)}`
  } else if (elementRelationship === 'NEUTRAL') {
    elementValue = `-${Math.abs(element)}`
  } else if (elementRelationship === 'DRAW') {
    elementValue = '0'
  } else {
    // AGC: Fallback to actual element bonus value if relationship can't be determined
    elementValue = element !== 0 ? (element > 0 ? `+${element}` : `${element}`) : '0'
  }
  
  const levelPart = level !== 0 ? ` + Level ${level}` : ''
  const elementPart = ` + ELEMENT: (${elementStatus}) ${elementValue}`
  const total = base + level + element

  return {
    summary: `HERO: (${heroStatus}) ${base}${levelPart}${elementPart} = ${total}`,
    baseTotal: total,
  }
}

const buildBuffSummary = (
  breakdown: TurnBreakdownSummary,
  baseTotal: number,
  buffs?: Array<{ label: string; status?: 'active' | 'ready' | 'used' }>,
) => {
  // AGC: FIX 2025-11-29 - Show buff as percentage multiplier when mana is active
  const pierce = Math.round(breakdown.pierceReduction ?? 0)
  const haste = Math.round(breakdown.hasteBonus ?? 0)
  const manaBoost = breakdown.manaBoost && breakdown.manaBoost > 1.0 ? breakdown.manaBoost : null
  const contributions: string[] = []
  let runningTotal = baseTotal

  if (pierce !== 0) {
    runningTotal += pierce
    // AGC: Calculate percentage for PIERCE (base 25%, boosted by mana)
    if (manaBoost && baseTotal > 0) {
      const basePiercePercent = 25
      const manaPercent = Math.round((manaBoost - 1.0) * 100)
      const totalPercent = basePiercePercent + manaPercent
      contributions.push(`PIERCE (${basePiercePercent}%+${manaPercent}% Mana = ${totalPercent}%) ${formatSigned(pierce)} ⚡`)
    } else if (baseTotal > 0) {
      contributions.push(`PIERCE (25%) ${formatSigned(pierce)} ⚡`)
    } else {
      contributions.push(`${formatSigned(pierce)} from PIERCE ⚡`)
    }
  }

  if (haste !== 0) {
    runningTotal += haste
    // AGC: Calculate percentage for HASTE (flat +5, boosted by mana)
    if (manaBoost) {
      const manaPercent = Math.round((manaBoost - 1.0) * 100)
      contributions.push(`HASTE (+${manaPercent}% Mana) ${formatSigned(haste)} ⚡`)
    } else {
      contributions.push(`HASTE ${formatSigned(haste)} ⚡`)
    }
  }

  const highlightedBuffs =
    buffs
      ?.filter((buff) => buff.status === 'active' || buff.status === 'used')
      .map((buff) => buff.label.toUpperCase()) ?? []

  const numericSegment = contributions.length > 0 ? `${contributions.join(' + ')} = ${runningTotal}` : null
  const labelSegment = highlightedBuffs.length > 0 && contributions.length === 0 ? highlightedBuffs.join(', ') : null
  const summarySegments = [labelSegment, numericSegment].filter(Boolean)

  return {
    summary: summarySegments.length > 0 ? summarySegments.join(' | ') : 'none',
    total: runningTotal,
  }
}

const buildRaritySummary = (breakdown: TurnBreakdownSummary, currentTotal: number) => {
  const rarityAmount = Math.round(breakdown.rarityBonus ?? 0)
  const rarityName = (breakdown.rarityName ?? 'none').toUpperCase()

  if (rarityAmount === 0) {
    return {
      summary: rarityName === 'NONE' ? 'none' : rarityName,
      total: currentTotal,
    }
  }

  const total = currentTotal + rarityAmount
  return {
    summary: `${rarityName} ${formatSigned(rarityAmount)} = ${total}`,
    total,
  }
}

const buildSpecialEffectLines = (type: 'you' | 'opponent', breakdown: TurnBreakdownSummary, otherBreakdown?: TurnBreakdownSummary) => {
  const messages: Array<{ text: string; tone: 'danger' | 'info' | 'success' }> = []

  // AGC: Mana potion boost (NEW - 2025-11-28) - Shows rarity-scaled percentage
  // AGC: FIX - Mana message should appear in YOUR breakdown EVERY TURN it's active
  // AGC: The manaBoost is stored in opponent's breakdown (because it affects their damage to you)
  // AGC: So we check otherBreakdown (opponent's) when rendering YOUR side
  if (type === 'you' && otherBreakdown?.manaBoost && otherBreakdown.manaBoost > 1.0) {
    const manaPercent = ((otherBreakdown.manaBoost - 1.0) * 100).toFixed(1).replace(/\.0$/, '')
    // AGC: Check if YOUR buff was activated this turn (shield/pierce effects are in otherBreakdown)
    const buffWasActivated = otherBreakdown?.shieldReduction || breakdown?.pierceReduction
    const message = buffWasActivated
      ? `💧 Mana Potion boosting your buff! +${manaPercent}%`
      : `💧 Mana Potion active! +${manaPercent}% on next buff`
    messages.push({
      text: message,
      tone: 'success',
    })
  }

  // AGC: Buff effects - Shield message shows mana bonus inline
  if (type === 'opponent' && breakdown.shieldReduction && breakdown.shieldReduction > 0) {
    const manaPercent = breakdown.manaBoost && breakdown.manaBoost > 1.0 
      ? ((breakdown.manaBoost - 1.0) * 100).toFixed(1).replace(/\.0$/, '')
      : null
    const manaNote = manaPercent ? ` (+${manaPercent}% Mana)` : ''
    messages.push({
      text: `Blocked by your SHIELD! -${Math.round(breakdown.shieldReduction)} damage${manaNote}`,
      tone: 'info',
    })
  }

  if (type === 'you' && breakdown.shieldReduction && breakdown.shieldReduction > 0) {
    const manaPercent = breakdown.manaBoost && breakdown.manaBoost > 1.0 
      ? ((breakdown.manaBoost - 1.0) * 100).toFixed(1).replace(/\.0$/, '')
      : null
    const manaNote = manaPercent ? ` (+${manaPercent}% Mana)` : ''
    messages.push({
      text: `Blocked by opponent's shield -${Math.round(breakdown.shieldReduction)} damage${manaNote}`,
      tone: 'danger',
    })
  }

  if (type === 'you' && breakdown.frostReduction && breakdown.frostReduction > 0) {
    messages.push({
      text: `Frozen by bot! -${Math.round(breakdown.frostReduction)} damage ❄️`,
      tone: 'danger',
    })
  }

  if (type === 'opponent' && breakdown.frostReduction && breakdown.frostReduction > 0) {
    messages.push({
      text: `Frozen by you! -${Math.round(breakdown.frostReduction)} damage ❄️`,
      tone: 'info',
    })
  }

  // AGC: Potion effects (NEW - 2025-11-25)
  if (breakdown.potionHealing && breakdown.potionHealing > 0) {
    messages.push({
      text: `🧪 Healing Potion! +${Math.round(breakdown.potionHealing)} energy`,
      tone: 'success',
    })
  }

  if (breakdown.potionPoisonReceived && breakdown.potionPoisonReceived > 0) {
    // AGC: Show poison damage with 1 decimal place (e.g., 2.7 instead of rounding to 3)
    const poisonDmg = breakdown.potionPoisonReceived % 1 === 0 
      ? Math.round(breakdown.potionPoisonReceived) 
      : breakdown.potionPoisonReceived.toFixed(1)
    messages.push({
      text: `☠️ Poisoned! -${poisonDmg} energy`,
      tone: 'danger',
    })
  }

  if (breakdown.potionShieldReduction && breakdown.potionShieldReduction > 0) {
    messages.push({
      text: `🧪 Shield Potion! -${Math.round(breakdown.potionShieldReduction)} damage blocked`,
      tone: 'info',
    })
  }

  // AGC: Double Strike potion effect (NEW - 2025-11-27)
  if (breakdown.doubleStrikeBonus && breakdown.doubleStrikeBonus > 0) {
    messages.push({
      text: `⚔️ Double Strike! +${Math.round(breakdown.doubleStrikeBonus)} damage (+${breakdown.doubleStrikePercent || 25}%)`,
      tone: 'success',
    })
  }

  return messages
}

const formatSigned = (value: number) => {
  const rounded = Math.abs(Math.round(value))
  const sign = value >= 0 ? '+' : '-'
  return `${sign}${rounded}`
}

const clampPercentage = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0
  }
  return Math.max(0, Math.min(100, value))
}

const getEnergyBarClass = (value?: number) => {
  if (typeof value !== 'number') {
    return 'bg-red-500'
  }
  if (value >= 75) {
    return 'bg-emerald-400'
  }
  if (value >= 50) {
    return 'bg-yellow-300'
  }
  if (value >= 25) {
    return 'bg-orange-400'
  }
  return 'bg-red-500'
}

